import { formatNumber } from 'src/utils/formatNumber/formatNumber';
import {
  calculateReferralPercentage,
  getReferralCounts,
} from './calculateReferralPercentage';

export type StrategyType =
  | 'PATIENT_JOURNEY'
  | 'TREATING_VOLUME'
  | 'NOT_REFERRING'
  | 'REFERRAL'
  | string;

export interface MetricDisplayResult {
  value: number | string | null;
  label: string;
}

interface MemberMetrics {
  patients?: number;
  sites?: number;
  step_0_claims?: number;
  [key: string]: number | undefined;
}

interface StrategyMetricResult {
  id: number;
  label: string;
  display_options: { code: string; type: string; label: string }[];
  extras?: {
    partially_sending_denominator?: boolean;
    partially_sending_numerator?: boolean;
  };
}

interface GetStrategyMetricDisplayParams {
  type?: StrategyType;
  metrics?: MemberMetrics | null;
  metricsV2?: Record<string, Record<string, number>>;
  strategyMetricResults?: StrategyMetricResult[];
}

/**
 * Centralized function to determine the metric value and label for a strategy member card.
 *
 * Handles all strategy types:
 * - Patient Journey: patients or sites count
 * - Treating Volume: claims count
 * - Not Referring (outbound): percentage referred elsewhere
 * - Referral (inbound/outbound): referral counts
 */
export const getStrategyMetricDisplay = ({
  type,
  metrics,
  metricsV2,
  strategyMetricResults,
}: GetStrategyMetricDisplayParams): MetricDisplayResult => {
  const isNotReferring = type === 'NOT_REFERRING';

  // Priority 1: NOT_REFERRING - show percentage referred elsewhere
  if (isNotReferring && strategyMetricResults && metricsV2) {
    const percentage = calculateReferralPercentage(
      strategyMetricResults,
      metricsV2,
      'claims'
    );

    if (percentage != null) {
      return {
        value: `${percentage.toFixed(0)}%`,
        label: 'referred elsewhere',
      };
    }
  }

  // Priority 2: Use first metric column from metricsV2 (first display option, defaults to claims)
  const firstMetric = strategyMetricResults?.[0];
  if (firstMetric && metricsV2) {
    const option = firstMetric.display_options?.[0];
    if (option) {
      const metricValues = metricsV2[firstMetric.id];
      const rawValue =
        metricValues != null
          ? typeof metricValues === 'object'
            ? metricValues[option.code]
            : metricValues
          : undefined;

      if (rawValue != null) {
        return {
          value: formatNumber(rawValue),
          label: firstMetric.label ?? '',
        };
      }
    }
  }

  // Priority 3: Legacy metrics fallback - patients
  if (metrics?.patients != null) {
    return {
      value: formatNumber(metrics.patients),
      label: strategyMetricResults?.[0]?.label ?? '',
    };
  }

  // Priority 4: Legacy metrics fallback - sites
  if (metrics?.sites != null) {
    return {
      value: formatNumber(metrics.sites),
      label: strategyMetricResults?.[0]?.label ?? '',
    };
  }

  // Priority 5: Legacy metrics fallback - step_*_claims
  const claimsEntry = Object.entries(metrics ?? {}).find(([key]) =>
    /^step_\d+_claims$/.test(key)
  );

  if (claimsEntry) {
    const [claimsKey, claimsCount] = claimsEntry;
    const count = claimsCount as number;

    const stepIndex = parseInt(
      claimsKey.match(/^step_(\d+)_claims$/)?.[1] ?? '0',
      10
    );

    return {
      value: formatNumber(count),
      label: strategyMetricResults?.[stepIndex]?.label ?? '',
    };
  }

  // No valid metric found
  return {
    value: null,
    label: '',
  };
};

interface NotReferringSubtitleParams {
  metricsV2?: Record<string, Record<string, number>>;
  strategyMetricResults?: any[];
}

/**
 * Get the "X of Y Referrals" subtitle for NOT_REFERRING strategy members
 * when there's no added_at date.
 */
export const getNotReferringSubtitle = ({
  metricsV2,
  strategyMetricResults,
}: NotReferringSubtitleParams): string | null => {
  if (!metricsV2 || !strategyMetricResults) return null;

  const referralCounts = getReferralCounts(
    strategyMetricResults,
    metricsV2,
    'claims'
  );

  if (!referralCounts) return null;

  const elsewhereCount = referralCounts.denominator - referralCounts.numerator;
  return `${elsewhereCount} of ${referralCounts.denominator} Referrals`;
};
